/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useContext, useEffect, useState } from "react";
import CustomInput from "../../inputs/customInput";
import { useTheme } from "@emotion/react";
import DateInput from "../../inputs/dateInput";
import dayjs from "dayjs";
import TransferTypeDropdown from "../../dropdowns/transferTypeDropdown";
import WalletTransferService from "../../../services/walletTransferService";
import { AuthContext } from "../../../provider/authProvider";
import BranchDropdown from "../../dropdowns/branchDropdown";

const UpdateTransferBox = ({
    handleReload,
    setPopupMsg,
    setPopupType,
    setShowPopup,
    transfer }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { user } = useContext(AuthContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [formValues, setFormValues] = useState({
        amount: transfer.amount,
        note: transfer.note,
        type: transfer.type,
        branchId: transfer.branch && transfer.branch.id
    });

    const [selectedType, setSelectedType] = useState(transfer.type);
    const [selectedBranch, setSelectedBranch] = useState(transfer.branch);
    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/,/g, "");

        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: numericValue,
        }));
    };

    useEffect(() => {
        if (transfer) {
            setFormValues({
                amount: transfer.amount || 0,
                note: transfer.note || '',
                type: transfer.type || null,
                branchId: transfer.branch?.id || null,
            });
            setSelectedType(t(transfer.type) || null);
            setSelectedBranch(transfer.branch || null);

            const parsedDate = dayjs(transfer.date, "DD/MM/YYYY hh:mm");
            if (parsedDate.isValid()) {
                setSelectedDate(parsedDate);
            } else {
                console.error("Invalid date format");
            }

            setUpdateErrors({});
        }
    }, [transfer]);


    const validateForm = () => {
        const newUpdateErrors = {};

        if (isNaN(formValues.amount)) {
            newUpdateErrors.amount = t('amountNumber');
        }

        setUpdateErrors(newUpdateErrors);

        return Object.keys(newUpdateErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await WalletTransferService.update({
                    id: transfer.id,
                    ...formValues,
                    date: selectedDate
                });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('update');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({
                        amount: 0,
                        note: '',
                        type: null
                    });
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                px: isMobile ? 1 : 4,
                py: 1
            }}
        >
            {/* <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                    {t('updateTransfer')}
                </Typography>
            </Box> */}

            {allBranchPermission && <BranchDropdown
                helperText={updateErrors.branch}
                error={!!updateErrors.branch}
                setFormValues={setFormValues}
                isFilter={false}
                selectedBranch={selectedBranch || null}
                setSelectedBranch={setSelectedBranch}
            />}

            <TransferTypeDropdown
                helperText={updateErrors.type}
                error={!!updateErrors.type}
                setFormValues={setFormValues}
                isFilter={false}
                selectedType={selectedType || null}
                setSelectedType={setSelectedType}
            />


            <CustomInput
                label={t('amount')}
                placeholder={t('amountHere')}
                name="amount"
                type='text'
                value={formValues.amount ? Number(formValues.amount).toLocaleString() : 0}
                error={!!updateErrors.amount}
                helperText={updateErrors.amount}
                onChange={handleNumberChange}
            />

            <DateInput
                label={t('date')}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                handleDateChange={handleDateChange}
            />

            <CustomInput
                label={t('note')}
                placeholder={t('noteHere')}
                name="note"
                type='text'
                value={formValues.note}
                onChange={handleChange}
            />

            {updateErrors.general && (
                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                    {updateErrors.general}
                </Typography>
            )}

            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                mt: updateErrors.general ? 1 : 2
            }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                >
                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <p>{t('update')}</p>
                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                        </Box>
                    }
                </Button>
            </Box>
        </Box>
    );
}

export default UpdateTransferBox;
